// @ts-strict-ignore
import {useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'common/i18n/hooks';
import {ExpansionToggleLabelMode, Section, SectionItem, SelectedTheme} from 'common/components/items-menu/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {TagItem} from 'common/components/tag-input/tag-item';
import {useCohorts} from 'common/contexts/react/cohorts-context';
import {
  fetchAllPropertyDefinitionsForGroupProfiles,
  fetchPropertyDefinitionsForUserProfiles,
  groupPropertyDefinitionsStore,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {useDataGroups} from 'common/contexts/react/data-group-context';
import {Translatable} from 'common/i18n';
import {BehaviorEntity as Behavior} from 'app_api/projects/behaviors/types';
import {getDataGroupLabel} from 'common/report/insights/util/events';
import {MetricType} from 'common/types/reports/bookmark';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {LocalizationStore} from 'common/i18n/store';
import {hasFirstClassMetrics} from 'apps/lexicon/src/lexicon-app/metrics/utils';
import {ICON_MAPPING} from 'common/query-builder/query-entry-section/util/entry-icon';
import {Metric} from 'app_api/projects/metrics/types';
import {findKeyByValue} from 'common/util/typescript';
import {BehaviorTypeIcons, OldMetricTypeIcons} from 'common/report/insights/util/metrics';

import {
  getEventSelectedLabel,
  getEventsSections,
  getSearchSelectedTagItems,
  getSearchTagMenuSections,
  getTagsSection,
  isEventItem,
} from '../events-menu/util';
import {useChildMenuCache} from '../child-menu';
import {EventItem, EventMenuItem} from '../events-menu/types';
import {getSavedBehaviorSections, useSavedBehaviors} from '../behaviors-menu/utils';
import {getFormulaSections, useSavedFormulas} from '../formulas-menu/utils';
import {getSerializedCohorts} from '../properties-menu/util';
import {template as noTagsTemplate} from '../events-menu/no-tags';
import {FormulaMenuItemValue} from '../formulas-menu/types';
import {TOP_PEOPLE} from '../properties-menu/constants';
import {NUM_RECENT_ITEMS} from '../constants';
import {isSavedCohort} from '../properties-menu/types';
import {convertDataGroupIdToSet} from '../utils/data-group-util';
import {
  CategoryToMetricType,
  CohortItemValue,
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  isSimpleMenuSectionItem,
  MenuType,
  MenuTypeToMetricType,
  MultiMetricsSection,
  MultiMetricsSectionItem,
  ProfileItemValue,
  SidebarCategory,
  SidebarItem,
  SidebarItemType,
} from './types';
import {
  getCohortSections,
  getCreateCustomSections,
  getHistoricalProperties,
  getProfileSections,
  getRecentItems,
} from './utils';
import {getMetricsSections, useSavedMetrics} from './metrics';

export function useSelectedLabel({
  selected,
  dataGroupId,
}: {
  selected: MultiMetricsSectionItem;
  dataGroupId: string;
}): Translatable {
  const metrics = useMetrics();
  const dataGroupStore = useDataGroups();
  const cohortsStore = useCohorts();

  if (!selected) {
    return ({t}) => t(`query-builder.select-metric`);
  }

  let label: Translatable = ({t}) => t(`query-builder.select-metric`);
  if (isFunnelMenuSectionItem(selected) || isRetentionMenuSectionItem(selected) || isSimpleMenuSectionItem(selected)) {
    label = selected.value.name;
  } else if (isFormulaMenuSectionItem(selected)) {
    label =
      selected.value.name ||
      (selected.value.id && metrics.getOne(selected.value.id)?.value?.name) ||
      (({t}) => t(`common.loading`));
  } else if (isEventMenuSectionItem(selected) && isEventItem(selected.value)) {
    label = getEventSelectedLabel(selected.value, {
      includeNameOnRename: true,
      labelPrefix: selected.value?.labelPrefix,
    });
  } else if (isCohortMenuSectionItem(selected)) {
    if (isSavedCohort(selected.value)) {
      label = cohortsStore?.value[selected.value.value]?.name || selected.value.label;
    } else {
      label = selected.value.label;
    }
  } else if (isProfileMenuSectionItem(selected)) {
    if (dataGroupId) {
      const dataGroups = Object.values(dataGroupStore.value);
      label = getDataGroupLabel(dataGroupId, dataGroups || []);
    } else {
      label = TOP_PEOPLE.label;
    }
  } else if (isSavedMetricMenuSectionItem(selected)) {
    label = selected.value.name;
  }

  return label;
}

export function useSidebarSections({
  excludedMetricTypes,
  selectedCategory,
  selectedTagSet,
  dataGroupName,
  metricLevelDataGroups,
  isSidebarTagSectionExpanded,
}: {
  excludedMetricTypes: Set<MetricType>;
  selectedCategory: SidebarCategory;
  selectedTagSet: Set<string>;
  dataGroupName: string;
  metricLevelDataGroups: boolean;
  isSidebarTagSectionExpanded: boolean;
}): Array<Section<SidebarItem>> {
  const eventsController = useEventsController();
  const i18n = useTranslation();
  const currentProject = useCurrentProject();
  const isVerifiedContentEnabled = currentProject.get()?.value?.canUseVerifiedContent();

  const firstClassMetricsEnabled = hasFirstClassMetrics(currentProject);

  const {t} = i18n;
  const tagsSection = getTagsSection(eventsController);

  const iconMap = firstClassMetricsEnabled ? BehaviorTypeIcons : OldMetricTypeIcons;

  const sidebarTagSection: Section<SidebarItem> = {
    icon: `tag`,
    label: t(`query-builder.events-menu.sidebar-tags`),
    selectedTheme: SelectedTheme.Checkmark,
    expansionToggle: {
      isExpanded: isSidebarTagSectionExpanded,
      labelMode: ExpansionToggleLabelMode.Unchanged,
      alwaysShow: true,
    },
    items: tagsSection.items.map((tag): SectionItem<SidebarItem> => {
      return {
        label: tag.label,
        isSelected: selectedTagSet.has(tag.value.value),
        value: {
          type: SidebarItemType.EventTag,
          tag: tag.value.value,
        },
      };
    }),
    emptySectionRenderer: noTagsTemplate,
  };

  const allSectionItems: Array<SectionItem<SidebarItem, Obj<any>>> = [
    {
      label: t(`query-builder.events-menu.sidebar-all`),
      icon: `icon-grid`,
      value: {
        type: SidebarItemType.Category,
        category: SidebarCategory.All,
      },
      isSelected: selectedCategory === SidebarCategory.All,
    },
  ];

  if (isVerifiedContentEnabled) {
    allSectionItems.push({
      label: t(`query-builder.events-menu.sidebar-verified`),
      icon: `verified-outlined`,
      selectedTheme: SelectedTheme.Focus,
      value: {
        type: SidebarItemType.Category,
        category: SidebarCategory.Verified,
      },
      isSelected: selectedCategory === SidebarCategory.Verified,
    });
  }

  const categorySections: Array<Section<SidebarItem>> = [
    {
      selectedTheme: SelectedTheme.Focus,
      items: allSectionItems,
    },
    {
      selectedTheme: SelectedTheme.Focus,
      items: [
        {
          label: t(`query-builder.metrics.event`),
          icon: `event`,
          tooltip: {text: t(`query-builder.metrics.event-tooltip`), 'max-width': `250px`},
          value: {
            type: SidebarItemType.Category,
            category: SidebarCategory.Event,
          },
          isSelected: selectedCategory === SidebarCategory.Event,
        },
        {
          label: metricLevelDataGroups ? t(`mixology.profile_plural`) : dataGroupName,
          icon: iconMap[MetricType.People],
          tooltip: {text: t(`query-builder.metrics.profile-tooltip`), 'max-width': `250px`},
          value: {
            type: SidebarItemType.Category,
            category: SidebarCategory.Profile,
          },
          isSelected: selectedCategory === SidebarCategory.Profile,
        },
      ].filter((item) => !excludedMetricTypes.has(CategoryToMetricType[item.value.category])) as Array<
        SectionItem<SidebarItem>
      >,
    },
    {
      selectedTheme: SelectedTheme.Focus,
      items: [
        ...(firstClassMetricsEnabled
          ? [
              {
                label: t(`query-builder.metrics.metric`),
                icon: ICON_MAPPING[MetricType.SavedMetric],
                tooltip: {
                  text: t(`query-builder.metrics.saved-metric-tooltip`),
                  'max-width': `250px`,
                },
                value: {
                  type: SidebarItemType.Category,
                  category: SidebarCategory.Metric,
                },
                isSelected: selectedCategory === SidebarCategory.Metric,
              },
            ]
          : [
              {
                label: t(`query-builder.metrics.formula`),
                icon: ICON_MAPPING[MetricType.Formula],
                tooltip: {
                  text: t(`query-builder.metrics.formula-tooltip`),
                  'max-width': `250px`,
                },
                value: {
                  type: SidebarItemType.Category,
                  category: SidebarCategory.Formula,
                },
                isSelected: selectedCategory === SidebarCategory.Formula,
              },
            ]),
      ].filter((item) => !excludedMetricTypes.has(CategoryToMetricType[item.value.category])) as Array<
        SectionItem<SidebarItem>
      >,
    },
    {
      selectedTheme: SelectedTheme.Focus,
      items: [
        /** Funnel Behaviors section */
        {
          label: t(`query-builder.metrics.funnel`),
          icon: iconMap[MetricType.Funnel],
          tooltip: {
            text: t(`query-builder.metrics.funnel-tooltip`),
            'max-width': `250px`,
          },
          value: {
            type: SidebarItemType.Category,
            category: SidebarCategory.Funnel,
          },
          isSelected: selectedCategory === SidebarCategory.Funnel,
        },

        /** Retention Behaviors section */
        {
          label: t(`query-builder.metrics.retention`),
          icon: iconMap[MetricType.Retention],
          tooltip: {
            text: t(`query-builder.metrics.retention-tooltip`),
            'max-width': `250px`,
          },
          value: {
            type: SidebarItemType.Category,
            category: SidebarCategory.Retention,
          },
          isSelected: selectedCategory === SidebarCategory.Retention,
        },

        /** Simple Behaviors section */
        ...(firstClassMetricsEnabled
          ? [
              {
                label: t(`query-builder.metrics.simple`),
                icon: iconMap[MetricType.Simple],
                tooltip: {
                  text: t(`query-builder.metrics.simple-tooltip`),
                  'max-width': `250px`,
                },
                value: {
                  type: SidebarItemType.Category,
                  category: SidebarCategory.Simple,
                },
                isSelected: selectedCategory === SidebarCategory.Simple,
              },
            ]
          : []),

        /** Cohorts section */
        {
          label: t(`query-builder.metrics.cohort`),
          icon: `community`,
          tooltip: {text: t(`query-builder.metrics.cohort-tooltip`), 'max-width': `250px`},
          value: {
            type: SidebarItemType.Category,
            category: SidebarCategory.Cohort,
          },
          isSelected: selectedCategory === SidebarCategory.Cohort,
        },
      ].filter((item) => !excludedMetricTypes.has(CategoryToMetricType[item.value.category])) as Array<
        SectionItem<SidebarItem>
      >,
    },
  ];

  const sections = useMemo(
    () => [...categorySections, sidebarTagSection],
    [selectedCategory, dataGroupName, selectedTagSet, isSidebarTagSectionExpanded],
  );
  return sections;
}

export function useCreateCustomSections({excludedMetricTypes}: {excludedMetricTypes: Set<MetricType>}) {
  const firstClassMetricsEnabled = hasFirstClassMetrics(useCurrentProject());
  const sections = useMemo(() => getCreateCustomSections({excludedMetricTypes, firstClassMetricsEnabled}), []);
  return sections;
}

export function useSearchSelectedTagItems({selectedTagSet}): TagItem[] {
  const eventsController = useEventsController();
  const i18n = useTranslation();

  const tagItems = useMemo(
    () =>
      getSearchSelectedTagItems({
        selectedTagSet,
        i18n,
        eventsController,
      }),
    [selectedTagSet],
  );
  return tagItems;
}

export function useSearchTagMenuSections({selectedTagSet}): Array<Section<TagItem>> {
  const eventsController = useEventsController();
  const i18n = useTranslation();

  const sections = useMemo(
    () =>
      getSearchTagMenuSections({
        selectedTagSet,
        i18n,
        eventsController,
      }),
    [selectedTagSet],
  );
  return sections;
}

function useEventsSections({
  excludedMetricTypes,
  dataGroupId,
  excludeAutotrackEvents,
  excludeCustomEvents,
  searchFilter,
  searchVerified = false,
  selectedTagSet,
  selected,
  showAllEvents,
  showAnyEvent,
  showTopEvents,
  showSessionEvent,
  showSpecialDatasetProperty,
  userId,
  projectId,
  workspaceId,
}: {
  excludedMetricTypes?: Set<MetricType>;
  dataGroupId: string;
  excludeAutotrackEvents: boolean;
  excludeCustomEvents: boolean;
  searchFilter: string;
  searchVerified: boolean;
  selectedTagSet: Set<string>;
  selected: MultiMetricsSectionItem;
  showAllEvents: boolean;
  showAnyEvent: boolean;
  showTopEvents: boolean;
  showSessionEvent: boolean;
  showSpecialDatasetProperty: boolean;
  userId: number;
  projectId: number;
  workspaceId: number;
}): Array<MultiMetricsSection<EventMenuItem>> {
  if (excludedMetricTypes?.has(MetricType.Event)) {
    return [];
  }

  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  const eventsController = useEventsController();
  const i18n = useTranslation();

  const {print} = i18n;
  const sectionLabel: Translatable = ({t}) => t(`query-builder.multi-metrics-menu.section-events`);
  let recentSectionItems = getRecentItems(cache, MenuType.Event);
  if (searchVerified) {
    recentSectionItems = recentSectionItems.filter(
      (sectionItem) => isEventItem(sectionItem.value) && !!sectionItem.value?.verified,
    );
  }
  const eventsSections = getEventsSections({
    recentlyUsedEvents: recentSectionItems.map((sectionItem) => sectionItem.value as EventItem),
    eventsController,
    dataGroupIds: convertDataGroupIdToSet(dataGroupId),
    excludeAutotrackEvents,
    excludeCustomEvents,
    mainSectionLabel: print(sectionLabel),
    searchFilter,
    selectedTagSet,
    selected: selected && isEventMenuSectionItem(selected) ? (selected.value as EventItem) : null,
    showAllEvents,
    showAnyEvent,
    showTopEvents,
    showSessionEvent,
    showSpecialDatasetProperty,
    otherOptions: [],
    searchVerified,
  });

  return eventsSections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      menuType: MenuType.Event,
    })),
  }));
}

function useProfileSections({
  dataGroupId,
  metricLevelDataGroups,
  dataGroupIdToNameMap,
  excludedMetricTypes,
  i18n,
}: {
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
  dataGroupIdToNameMap: {[id: string]: string};
  excludedMetricTypes: Set<MetricType>;
  i18n: LocalizationStore;
}): {
  isLoading: boolean;
  sections: Array<MultiMetricsSection<ProfileItemValue>>;
} {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dataGroupStore = useDataGroups();
  const firstClassMetricsEnabled = hasFirstClassMetrics(useCurrentProject());

  const historicalProperties = useMemo(() => {
    if (isLoading) return [];
    if (metricLevelDataGroups) {
      // for every data group plus the user group, get historical properties
      const dataGroupIds = [``, ...Object.keys(dataGroupStore.value)];
      return dataGroupIds.reduce((acc, id) => {
        acc[id] = getHistoricalProperties(id, i18n);
        return acc;
      }, {});
    }
    return {[dataGroupId || ``]: getHistoricalProperties(dataGroupId, i18n)};
  }, [
    dataGroupId,
    metricLevelDataGroups,
    isLoading,
    Object.keys(dataGroupStore.value).length,
    Object.keys(groupPropertyDefinitionsStore.value).length,
    Object.keys(peoplePropertyDefinitionsStore.value).length,
  ]);

  useEffect(() => {
    const fetchGroupPropsIfMissing = async () => {
      if (Object.keys(groupPropertyDefinitionsStore.value).length === 0) {
        await fetchAllPropertyDefinitionsForGroupProfiles(dataGroupStore.value);
      }
    };
    const fetchUserPropsIfMissing = async () => {
      if (Object.keys(peoplePropertyDefinitionsStore.value).length === 0) {
        await fetchPropertyDefinitionsForUserProfiles({});
      }
    };

    const fetchProfileProperties = async () => {
      setIsLoading(true);
      if (metricLevelDataGroups) {
        await Promise.all([fetchGroupPropsIfMissing(), fetchUserPropsIfMissing()]);
      } else if (dataGroupId) {
        await fetchGroupPropsIfMissing();
      } else {
        await fetchUserPropsIfMissing();
      }
      setIsLoading(false);
    };
    fetchProfileProperties();
  }, [dataGroupId, metricLevelDataGroups]);

  if (excludedMetricTypes.has(MetricType.People)) {
    return {isLoading, sections: []};
  }
  const sections = getProfileSections({
    dataGroupIdToNameMap,
    metricLevelDataGroups,
    firstClassMetricsEnabled,
    historicalProperties,
  });
  return {isLoading, sections};
}

function useMetricsSections({
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
  searchVerified,
}: {
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
  searchVerified: boolean;
}): Array<MultiMetricsSection<Metric>> {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  let savedMetrics = excludedMetricTypes.has(MetricType.SavedMetric) ? [] : useSavedMetrics(excludedMetricTypes);
  let savedFormulas = excludedMetricTypes.has(MetricType.Formula) ? [] : useSavedFormulas();
  let recentSectionItems = [
    ...getRecentItems(cache, MenuType.SavedMetric),
    ...getRecentItems(cache, MenuType.Formula),
  ].slice(0, NUM_RECENT_ITEMS);

  if (searchVerified) {
    savedMetrics = savedMetrics.filter((metric) => {
      return !!metric.verified;
    });
    savedFormulas = savedFormulas.filter((metric) => {
      return !!metric.verified;
    });
    recentSectionItems = recentSectionItems.filter((sectionItem) => !!sectionItem.value?.verified);
  }

  return getMetricsSections({
    recentMetricItems: recentSectionItems.map((sectionItem) => sectionItem.value),
    savedMetrics,
    savedFormulas,
  });
}

function useFormulaSections({
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
  searchVerified,
}: {
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
  searchVerified: boolean;
}): Array<MultiMetricsSection<FormulaMenuItemValue>> {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  let savedFormulas = useSavedFormulas();
  let recentSectionItems = getRecentItems(cache, MenuType.Formula);

  if (excludedMetricTypes.has(MetricType.Formula)) {
    return [];
  }

  if (searchVerified) {
    savedFormulas = savedFormulas.filter((formula) => {
      return !!formula.verified;
    });
    recentSectionItems = recentSectionItems.filter((sectionItem) => !!sectionItem.value?.verified);
  }

  return getFormulaSections({
    recentFormulaMenuItems: recentSectionItems.map((sectionItem) => sectionItem.value),
    savedFormulas,
  });
}

function useBehaviorSection({
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
  metricType,
}: {
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
  metricType: MetricType.Funnel | MetricType.Retention | MetricType.Simple;
}): Array<MultiMetricsSection<Behavior>> {
  const currentProject = useCurrentProject();
  const firstClassMetricsEnabled = hasFirstClassMetrics(currentProject);
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  const savedBehaviors = useSavedBehaviors({metricType});
  const menuType = findKeyByValue(MenuTypeToMetricType, metricType) as
    | MenuType.Funnel
    | MenuType.Retention
    | MenuType.Simple;
  const recentSectionItems = getRecentItems(cache, menuType);

  if (excludedMetricTypes.has(metricType)) {
    return [];
  }
  return getSavedBehaviorSections({
    firstClassMetricsEnabled,
    metricType,
    recentBehaviorMenuItems: recentSectionItems.map((sectionItem) => sectionItem.value),
    savedBehaviors,
  });
}

function useCohortsSections({
  dataGroupId,
  metricLevelDataGroups,
  dataGroupIdToNameMap,
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
}: {
  dataGroupId: string;
  metricLevelDataGroups: boolean;
  dataGroupIdToNameMap: {[id: string]: string};
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
}): {
  isLoading: boolean;
  sections: Array<MultiMetricsSection<CohortItemValue>>;
} {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const cohortsStore = useCohorts();
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});

  const cohortsByDataGroupId = useMemo(() => {
    if (isLoading) return [];
    const dataGroupIds = metricLevelDataGroups ? Object.keys(dataGroupIdToNameMap) : [dataGroupId];
    return dataGroupIds.reduce((acc, id) => {
      const cohorts = getSerializedCohorts(cohortsStore, id);
      if (cohorts.length > 0) {
        acc[id] = cohorts;
      }
      return acc;
    }, {});
  }, [dataGroupId, isLoading, Object.values(cohortsStore.value).length]);
  const recentSectionItems = getRecentItems(cache, MenuType.Cohort);

  useEffect(() => {
    const fetchCohorts = async () => {
      setIsLoading(true);
      const params = {projectId, workspaceId};
      if (metricLevelDataGroups) {
        // for every data group plus the user group, get cohorts
        const dataGroupIds = Object.keys(dataGroupIdToNameMap);
        await Promise.all(
          dataGroupIds.map((id) => {
            if (id === ``) {
              return cohortsStore.fetch(params);
            }
            return cohortsStore.fetch({...params, dataGroupId: id}, {}, id);
          }),
        );
      } else if (dataGroupId) {
        await cohortsStore.fetch({...params, dataGroupId}, {}, dataGroupId);
      } else {
        await cohortsStore.fetch(params);
      }
      setIsLoading(false);
    };
    fetchCohorts();
  }, [dataGroupId]);

  if (excludedMetricTypes.has(MetricType.Cohort)) {
    return {isLoading, sections: []};
  }
  const sections = getCohortSections({
    recentCohortSectionItems: recentSectionItems,
    dataGroupIdToNameMap,
    userId,
    cohortsByDataGroupId,
    metricLevelDataGroups,
  });
  return {isLoading, sections};
}

export function useMultiMetricsSections({
  category,
  dataGroupId,
  metricLevelDataGroups,
  excludedMetricTypes,
  excludeAutotrackEvents,
  excludeCustomEvents,
  searchFilter,
  searchVerified,
  selectedTagSet,
  selected,
  showAllEvents,
  showAnyEvent,
  showTopEvents,
  showSessionEvent,
  showSpecialDatasetProperty,
  userId,
  projectId,
  workspaceId,
  i18n,
}: {
  category: SidebarCategory;
  dataGroupId: string;
  metricLevelDataGroups: boolean;
  excludedMetricTypes: Set<MetricType>;
  excludeAutotrackEvents: boolean;
  excludeCustomEvents: boolean;
  searchFilter: string;
  searchVerified: boolean;
  selectedTagSet: Set<string>;
  selected: MultiMetricsSectionItem;
  showAllEvents: boolean;
  showAnyEvent: boolean;
  showTopEvents: boolean;
  showSessionEvent: boolean;
  showSpecialDatasetProperty: boolean;
  userId: number;
  projectId: number;
  workspaceId: number;
  i18n: LocalizationStore;
}): {
  isLoading: boolean;
  sections: MultiMetricsSection[];
} {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});

  const currentProject = useCurrentProject();
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const eventsController = useEventsController();
  const {t} = useTranslation();
  const dataGroupStore = useDataGroups();
  const dataGroupIdToNameMap = useMemo(() => {
    const names = {'': t(`query-builder.dg-user`)};
    for (const [id, dataGroup] of Object.entries(dataGroupStore.value || {})) {
      names[id] = dataGroup.displayName;
    }
    return names;
  }, [Object.keys(dataGroupStore.value).length]);

  const eventsSections = useEventsSections({
    dataGroupId,
    excludeAutotrackEvents,
    excludeCustomEvents,
    searchFilter,
    searchVerified,
    selectedTagSet,
    selected,
    showAllEvents,
    showAnyEvent,
    showTopEvents,
    showSessionEvent,
    showSpecialDatasetProperty,
    userId,
    projectId,
    workspaceId,
    excludedMetricTypes,
  });
  const {isLoading: isLoadingProperties, sections: profileSections} = useProfileSections({
    dataGroupId,
    metricLevelDataGroups,
    dataGroupIdToNameMap,
    excludedMetricTypes,
    i18n,
  });
  const formulaSections = useFormulaSections({excludedMetricTypes, userId, projectId, workspaceId, searchVerified});
  const savedMetricsSections = useMetricsSections({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    searchVerified,
  });
  const funnelSections = useBehaviorSection({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    metricType: MetricType.Funnel,
  });
  const retentionSections = useBehaviorSection({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    metricType: MetricType.Retention,
  });
  const simpleBehaviorSections = useBehaviorSection({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    metricType: MetricType.Simple,
  });
  const {isLoading: isCohortsLoading, sections: cohortsSections} = useCohortsSections({
    dataGroupId,
    metricLevelDataGroups,
    dataGroupIdToNameMap,
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
  });

  const firstClassMetricsEnabled = hasFirstClassMetrics(currentProject);
  const isLoadingBehaviors = behaviors.isLoadingAll();
  const isLoadingMetrics = metrics.isLoadingAll();
  const isLoadingEvents = eventsController.loading;

  const isLoading = useMemo(() => {
    switch (category) {
      case SidebarCategory.Event:
        return isLoadingEvents;
      case SidebarCategory.Profile:
        return false;
      case SidebarCategory.Formula:
        return isLoadingMetrics;
      case SidebarCategory.Funnel:
        return isLoadingBehaviors;
      case SidebarCategory.Retention:
        return isLoadingBehaviors;
      case SidebarCategory.Simple:
        return isLoadingBehaviors;
      case SidebarCategory.Cohort:
        return isCohortsLoading;
      case SidebarCategory.Verified:
        return isLoadingEvents || isLoadingMetrics;
      default:
        return isCohortsLoading || isLoadingProperties || isLoadingBehaviors || isLoadingMetrics || isLoadingEvents;
    }
  }, [category, isCohortsLoading, isLoadingProperties, isLoadingBehaviors, isLoadingMetrics, isLoadingEvents]);
  const sections = useMemo(() => {
    if (isLoading) return [];
    if (selectedTagSet.size > 0) {
      if ([SidebarCategory.All, SidebarCategory.Event].includes(category)) {
        return eventsSections;
      }
      return [];
    }

    switch (category) {
      case SidebarCategory.Event:
        return eventsSections;
      case SidebarCategory.Profile:
        return profileSections;
      case SidebarCategory.Formula:
        return formulaSections;
      case SidebarCategory.Metric:
        return savedMetricsSections;
      case SidebarCategory.Funnel:
        return funnelSections;
      case SidebarCategory.Retention:
        return retentionSections;
      case SidebarCategory.Simple:
        return simpleBehaviorSections;
      case SidebarCategory.Cohort:
        return cohortsSections;
      case SidebarCategory.Verified: {
        const metricsSection = firstClassMetricsEnabled
          ? {
              label: ({t}) => t(`query-builder.multi-metrics-menu.section-saved-metrics`),
              items: savedMetricsSections.flatMap((section) => section.items),
            }
          : {
              label: ({t}) => t(`query-builder.multi-metrics-menu.verified.section-formulas`),
              items: formulaSections.flatMap((section) => section.items),
            };
        return [
          ...eventsSections,
          metricsSection,
          {
            label: ({t}) => t(`query-builder.multi-metrics-menu.verified.section-behaviors`),
            items: [
              ...funnelSections.flatMap((section) => section.items),
              ...retentionSections.flatMap((section) => section.items),
              ...simpleBehaviorSections.flatMap((section) => section.items),
            ].filter((item) => {
              return !!item.value.verified;
            }),
          },
        ];
      }
      default: {
        const formulaSectionItems = formulaSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
          if (section.items && !section.isRecentSection) {
            return [...sectionItems, ...section.items];
          }
          return sectionItems;
        }, []);
        const savedMetricSectionItems = firstClassMetricsEnabled
          ? savedMetricsSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
              if (section.items && !section.isRecentSection) {
                return [...sectionItems, ...section.items];
              }
              return sectionItems;
            }, [])
          : [];
        const funnelSectionItems = funnelSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
          if (section.items && !section.isRecentSection) {
            return [...sectionItems, ...section.items];
          }
          return sectionItems;
        }, []);
        const retentionSectionItems = retentionSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
          if (section.items && !section.isRecentSection) {
            return [...sectionItems, ...section.items];
          }
          return sectionItems;
        }, []);
        const simpleBehaviorSectionItems = simpleBehaviorSections.reduce(
          (sectionItems: MultiMetricsSectionItem[], section) => {
            if (section.items && !section.isRecentSection) {
              return [...sectionItems, ...section.items];
            }
            return sectionItems;
          },
          [],
        );
        const metricsSection: MultiMetricsSection = {
          label: ({t}) => t(`query-builder.multi-metrics-menu.section-saved-metrics`),
          items: [
            ...savedMetricSectionItems,
            ...formulaSectionItems,
            ...funnelSectionItems,
            ...retentionSectionItems,
            ...simpleBehaviorSectionItems,
          ],
        };

        let recentSection = null;
        const allRecentItems = [
          MenuType.Event,
          MenuType.Profile,
          MenuType.Formula,
          MenuType.Funnel,
          MenuType.Retention,
          MenuType.Simple,
          MenuType.Cohort,
        ]
          .filter((menuType) => !excludedMetricTypes.has(MenuTypeToMetricType[menuType]))
          .flatMap((menuType) => getRecentItems(cache, menuType))
          .filter(Boolean);
        if (allRecentItems.length > 0) {
          recentSection = {
            isRecentSection: true,
            label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
            items: allRecentItems.slice(0, NUM_RECENT_ITEMS),
          };
        }

        return [
          ...(recentSection ? [recentSection] : []),
          metricsSection,
          ...eventsSections.filter((section) => !section.isRecentSection),
          ...profileSections,
          ...cohortsSections
            .filter((section) => !section.isRecentSection)
            .map((section) => ({...section, label: ({t}) => t(`query-builder.multi-metrics-menu.section-cohorts`)})),
        ].filter(Boolean);
      }
    }
  }, [
    dataGroupId,
    category,
    selected,
    isLoading,
    eventsSections,
    profileSections,
    formulaSections,
    funnelSections,
    retentionSections,
    cohortsSections,
  ]);

  return {
    isLoading,
    sections,
  };
}
